import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon, ClockIcon, XMarkIcon, CheckCircleIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import AcademicLicensePopUp from '../SignUp/AcademicLicensePage';
import AcademicRenewalForm, { RenewalFormData } from './AcademicRenewalForm';
import AcademicCancellationForm, { CancellationFormData } from './AcademicCancellationForm';
import { AcademicExpirationStatus } from '../../services/AcademicExpirationService';
import { useNavigate } from 'react-router-dom';
import SharedButton from '@/global/Button/Button';

interface RenewalResult {
  success: boolean;
  message: string;
}

interface AcademicExpirationModalProps {
  open: boolean;
  onClose: () => void;
  warningType: string | null;
  expirationStatus: AcademicExpirationStatus | null;
  onRenewal: (formData?: RenewalFormData) => Promise<RenewalResult>;
  onCancellation: (formData?: CancellationFormData) => Promise<RenewalResult>;
  loading: boolean;
  onClearPendingConfirmation?: () => void;
  onRefreshUserAttributes?: () => Promise<void>;
  // New props for additional states
  hasAppliedForRenewal?: boolean;
  hasCancelledAccount?: boolean;
  wasRenewalDenied?: boolean;
  wasRenewalApproved?: boolean;
  currentAcademicPlan?: string | null;
}

interface ModalContent {
  title: string;
  icon: JSX.Element;
  message: string;
  urgency: 'high' | 'medium';
}

/**
 * Modal component for academic expiration warnings
 */
const AcademicExpirationModal: React.FC<AcademicExpirationModalProps> = ({
  open,
  onClose,
  warningType,
  expirationStatus,
  onRenewal,
  onCancellation,
  loading,
  onClearPendingConfirmation,
  onRefreshUserAttributes,
  hasAppliedForRenewal = false,
  hasCancelledAccount = false,
  wasRenewalDenied = false,
  wasRenewalApproved = false,
  currentAcademicPlan = null,
}) => {

  const [showAcademicLicensePopup, setShowAcademicLicensePopup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showRenewalForm, setShowRenewalForm] = useState(false);
  const [showCancellationForm, setShowCancellationForm] = useState(false);
  const navigate = useNavigate();

  if (!open || !warningType || !expirationStatus) return null;

  const { isExpired, daysUntilExpiration, gracePeriodDaysRemaining } = expirationStatus;

  const handleRenewalClick = () => {
    if (!acceptedTerms) {
      Swal.fire({
        title: 'Terms Required',
        text: 'Please accept the DISGENET Academic License to proceed.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
      });
      return;
    }
    setShowRenewalForm(true);
  };

  const handleRenewalFormSubmit = async (formData: RenewalFormData) => {
    try {
      const result = await onRenewal(formData);
      setShowRenewalForm(false);
      
      if (result.success) {
        Swal.fire({
          title: 'Renewal Request Submitted',
          text: 'We have sent a confirmation email to your registered email address. Please check your inbox and click the confirmation link to proceed with your renewal.',
          icon: 'success',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'btn-gradient-pink',
          },
        });
      }
    } catch (error) {
      console.error('Error submitting renewal form:', error);
    }
  };

  const handleRenewalFormClose = () => {
    setShowRenewalForm(false);
  };

  const handleCancellationClick = () => {
    if (!acceptedTerms) {
      Swal.fire({
        title: 'Terms Required',
        text: 'Please accept the DISGENET Academic License to proceed.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3B82F6',
      });
      return;
    }
    setShowCancellationForm(true);
  };

  const handleCancellationFormSubmit = async (formData: CancellationFormData) => {
    try {
      setShowCancellationForm(false);
      
      const confirmResult = await Swal.fire({
        title: 'Confirm Account Cancellation',
        text: 'Are you sure you want to cancel your academic account? This action cannot be undone and your account will be permanently deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Cancel Account',
        cancelButtonText: 'Keep Account',
        customClass: {
          confirmButton: 'btn-gradient-pink',
          cancelButton: 'btn-secondary-blue',
        },
      });

      if (confirmResult.isConfirmed) {
        const result = await onCancellation(formData);

        if (result.success) {
          Swal.fire({
            title: 'Account Cancelled',
            text: result.message,
            icon: 'success',
            customClass: {
              confirmButton: 'btn-secondary-blue',
            },
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: result.message,
            icon: 'error',
            customClass: {
              confirmButton: 'btn-secondary-blue',
            },
          });
        }
      }
    } catch (error) {
      console.error('Error submitting cancellation form:', error);
    }
  };

  const handleCancellationFormClose = () => {
    setShowCancellationForm(false);
  };

  const getModalContent = (): ModalContent | null => {
    switch (warningType) {
      case 'waiting-confirmation':
        return {
          title: 'Waiting for Email Confirmation',
          icon: <ClockIcon className="w-12 h-12 text-blue-500" />,
          message:
            'We have sent a confirmation email to your registered email address. Please check your inbox and click the confirmation link to proceed with your renewal request.',
          urgency: 'medium',
        };
      case 'reviewing-application':
        return {
          title: 'Application Under Review',
          icon: <ClockIcon className="w-12 h-12 text-blue-500" />,
          message:
            'Your renewal application is currently being reviewed by our team. We will notify you once a decision has been made.',
          urgency: 'medium',
        };
      case 'account-cancelled':
        return {
          title: 'Account Cancelled',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-orange-500" />,
          message: `You've already cancelled your subscription. You have ${daysUntilExpiration} days remaining in your current plan. You're still on time to renew your account. If you've changed your mind, contact us.`,
          urgency: 'medium',
        };
      case 'renewal-denied':
        return {
          title: 'Application Denied',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />,
          message:
            "Your application has been denied as it doesn't look like you're currently affiliated with an academic institution.",
          urgency: 'high',
        };
      case 'renewal-approved':
        return {
          title: 'Renewal Approved!',
          icon: <CheckCircleIcon className="w-12 h-12 text-green-500" />,
          message: `Your account has been approved and you are now on the ${currentAcademicPlan} plan. If you want to upgrade to a higher plan, you can do so from the academic plans page.`,
          urgency: 'medium',
        };
      case 'expired':
        if (gracePeriodDaysRemaining > 0) {
          return {
            title: 'Account Expired',
            icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />,
            message: `Your account has expired. You have ${gracePeriodDaysRemaining} days remaining to renew your access or your account will be permanently deleted.`,
            urgency: 'high',
          };
        } else {
          return {
            title: 'Account Expired',
            icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />,
            message: `Your account has expired. You can still renew your access to continue using our services.`,
            urgency: 'high',
          };
        }
      case 'critical':
        return {
          title: 'Account Expiring Soon',
          icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />,
          message: `Your account will expire in ${daysUntilExpiration} days. Please renew your access to continue using our services.`,
          urgency: 'high',
        };
      case 'expiring-soon':
        return {
          title: 'Account Expiration Notice',
          icon: <ClockIcon className="w-12 h-12 text-yellow-500" />,
          message: `Your account will expire in ${daysUntilExpiration} days. We recommend renewing your access soon to avoid any interruption.`,
          urgency: 'medium',
        };
      case 'upgrade-available':
        return {
          title: 'Limited Academic Access',
          icon: <BellAlertIcon className="w-12 h-12 text-blue-500" />,
          message: `You are seeing only a fraction of data with your free academic license. To see more and access all features, update to: Standard or Advanced.`,
          urgency: 'medium',
        };
      default:
        return null;
    }
  };

  const handleRenewal = async () => {
    if (!acceptedTerms) {
      Swal.fire({
        title: 'Terms Required',
        text: 'Please accept the terms and conditions to proceed with renewal.',
        icon: 'warning',
        customClass: {
          confirmButton: 'btn-secondary-blue',
        },
      });
      return;
    }

    const result = await onRenewal();

    if (result.success) {
      Swal.fire({
        title: 'Renewal Process Initiated!',
        text: result.message,
        icon: 'success',
        customClass: {
          confirmButton: 'btn-secondary-blue',
        },
      });
    } else {
      Swal.fire({
        title: 'Error',
        text: result.message,
        icon: 'error',
        customClass: {
          confirmButton: 'btn-gradient-pink',
        },
      });
    }
  };

  const handleCancellation = async () => {
    const confirmResult = await Swal.fire({
      title: 'Confirm Account Cancellation',
      text: 'Are you sure you want to cancel your academic account? This action cannot be undone and your account will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel Account',
      cancelButtonText: 'Keep Account',
      customClass: {
        confirmButton: 'btn-gradient-pink',
        cancelButton: 'btn-secondary-blue',
      },
    });

    if (confirmResult.isConfirmed) {
      const result = await onCancellation();

      if (result.success) {
        Swal.fire({
          title: 'Account Cancelled',
          text: result.message,
          icon: 'success',
          customClass: {
            confirmButton: 'btn-secondary-blue',
          },
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: result.message,
          icon: 'error',
          customClass: {
            confirmButton: 'btn-gradient-pink',
          },
        });
      }
    }
  };

  const content = getModalContent();
  if (!content) return null;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white px-6 pb-6 pt-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 inline-flex justify-center rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* DISGENET Brand Header */}
            <div className="text-center mb-6">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-4">
                {content.icon}
              </div>
            </div>

            <div className="text-center">
              <DialogTitle as="h3" className="text-xl font-bold leading-6 text-gray-900 mb-4">
                {content.title}
              </DialogTitle>
              <div className="mb-2">
                <p className="text-gray-700 leading-relaxed">{content.message}</p>
              </div>
            </div>

            <div>
              {/* Terms and Conditions Section - Only show for normal renewal flows */}
              {!['waiting-confirmation', 'reviewing-application', 'account-cancelled', 'renewal-denied', 'renewal-approved', 'upgrade-available'].includes(
                warningType || '',
              ) && (
                <div className="p-2">
                  <div className="mt-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={e => setAcceptedTerms(e.target.checked)}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded mb-2"
                      />
                      <span className="ml-2 text-xs font-medium text-gray-800">
                        I accept the{' '}
                        <button
                          type="button"
                          onClick={() => setShowAcademicLicensePopup(true)}
                          className="text-xs font-medium font-bold transition-colors underline mb-2"
                        >
                          DISGENET Academic License.
                        </button>
                      </span>
                    </label>
                    <small className='mb-4'>
                      To continue with the renewal or cancellation, you will be required to complete a short
                      feedback form.
                    </small>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">

                {/* Upgrade Available in Academics*/}
                {warningType === 'upgrade-available' && (
                  <div className="space-y-3">
                    <SharedButton
                      type="button"
                      variant="gradient"
                      size="lg"
                      onClick={() => {
                        onClose();
                        navigate('/academic-plans');
                      }}
                      className="w-full"
                    >
                      View Plans
                    </SharedButton>
                  </div>
                )}

                {/* Waiting Confirmation */}
                {warningType === 'waiting-confirmation' && (
                  <>
                    <button
                      type="button"
                      onClick={() => window.open('/Support', '_blank')}
                      className="w-full inline-flex justify-center items-center rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-md hover:from-blue-600 hover:to-blue-800 hover:scale-105 transition-all duration-200"
                    >
                      Contact Support
                    </button>
                    {onRefreshUserAttributes && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await onRefreshUserAttributes();
                          } catch (error) {
                            console.error('Failed to refresh user attributes:', error);
                          }
                        }}
                        className="w-full inline-flex justify-center items-center rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-green-700 transition-all duration-200"
                      >
                        Check Email Confirmation Status
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (onClearPendingConfirmation) {
                          onClearPendingConfirmation();
                        }
                        onClose();
                      }}
                      className="w-full inline-flex justify-center items-center rounded-xl bg-gray-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-gray-700 transition-all duration-200"
                    >
                      I didn't receive the email
                    </button>
                  </>
                )}
                
                {/* Special States: Contact Support */}
                {['reviewing-application', 'account-cancelled', 'renewal-denied'].includes(warningType || '') && (
                  <button
                    type="button"
                    onClick={() => window.open('/Support', '_blank')}
                    className="w-full inline-flex justify-center items-center rounded-xl btn-gradient-color px-6 py-3 text-sm font-semibold text-white shadow-md hover:from-blue-600 hover:to-blue-800 hover:scale-105 transition-all duration-200"
                  >
                    Contact Support
                  </button>
                )}

                {/* Renewal Approved */}
                {warningType === 'renewal-approved' && (
                  // Upgrade and dismiss buttons for approved renewals
                    <SharedButton
                      type="button"
                      variant="gradient"
                      size="md"
                      onClick={async () => {
                        // First dismiss the modal to remove the renewal application tag
                        await onClose();
                        // Then open the upgrade page
                        window.open('/academic-plans', '_blank');
                      }}
                      className="w-full"
                    >
                      Upgrade Plan
                    </SharedButton>
                )}
                
                {/* Normal renewal buttons */}
                {!['waiting-confirmation', 'reviewing-application', 'account-cancelled', 'renewal-denied', 'renewal-approved', 'upgrade-available'].includes( warningType || '' ) && (
                  <>
                    <SharedButton
                      type="button"
                      variant="gradient"
                      size="lg"
                      onClick={handleRenewalClick}
                      disabled={loading || !acceptedTerms}
                      className={`w-full inline-flex justify-center items-center rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 ${
                        loading || !acceptedTerms
                          ? 'bg-gradient-to-r from-pink-400 to-purple-600 opacity-50 cursor-not-allowed'
                          : 'btn-gradient-color hover:from-pink-600 hover:to-purple-800 hover:scale-105'
                      }`}
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        'Renew Access'
                      )}
                    </SharedButton>

                    <SharedButton
                      type="button"
                      variant="secondary"
                      size="lg"
                      onClick={handleCancellationClick}
                      disabled={loading || !acceptedTerms}
                      className={`w-full inline-flex justify-center items-center rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 ${
                        loading || !acceptedTerms
                          ? 'bg-gray-400 opacity-50 cursor-not-allowed'
                          : 'bg-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {loading ? 'Processing...' : 'Cancel Account'}
                    </SharedButton>
                  </>
                )}

                <button
                  type="button"
                  onClick={onClose}
                  className="w-full inline-flex justify-center rounded-2xl bg-white px-6 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                >
                  {warningType === 'renewal-approved' ? 'Dismiss' : 'Remind Me Later'}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>

      {/* Academic License Popup */}
      {showAcademicLicensePopup && (
        <div className="fixed inset-0" style={{ zIndex: 9999 }} onClick={e => e.stopPropagation()}>
          <AcademicLicensePopUp
            onClose={() => {
              setShowAcademicLicensePopup(false);
              // Don't close the main modal, just the license popup
            }}
            onAcceptTerms={() => {
              setAcceptedTerms(true);
              setShowAcademicLicensePopup(false);
            }}
          />
        </div>
      )}

      {/* Academic Renewal Form */}
      <AcademicRenewalForm
        open={showRenewalForm}
        onClose={handleRenewalFormClose}
        onSubmit={handleRenewalFormSubmit}
        loading={loading}
      />

      {/* Academic Cancellation Form */}
      <AcademicCancellationForm
        open={showCancellationForm}
        onClose={handleCancellationFormClose}
        onSubmit={handleCancellationFormSubmit}
        loading={loading}
      />
    </Dialog>
  );
};

export default AcademicExpirationModal;
